import { useLocation } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, Btn, PillTag } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
} from "@/lib/store";

/**
 * NotificationPermission
 *
 * A first-launch prompt that explains exactly which notifications MNF will
 * send before the OS-level dialog appears. iOS and Android each only let you
 * ask the system permission once, so we precede it with this primer screen
 * so a sister can read what she is opting in to. The screen mirrors the
 * five categories disclosed in our Privacy Policy (Section 9).
 *
 * "Turn on" simulates the granted state. "Maybe later" leaves it unknown
 * and routes back to Today. We never call the real OS API in the demo,
 * but the production wrapper would call requestPermission here.
 */

interface CategoryRow {
  icon: string;
  label: string;
  copy: string;
  example: string;
}

const CATEGORIES: CategoryRow[] = [
  {
    icon: "menu_book",
    label: "Daily scripture reminder",
    copy: "A short verse at the time you choose, to start your morning with the Word.",
    example: "Today's verse, Psalm 23. Tap to read in the Garden.",
  },
  {
    icon: "favorite",
    label: "Prayer wall replies",
    copy: "When a sister hearts your request or leaves a note, you will know.",
    example: "A sister is praying for you.",
  },
  {
    icon: "calendar_today",
    label: "Weekly check-ins",
    copy: "Sunday evening nudge to log your week and tend your streak.",
    example: "It is time for your Sunday reflection.",
  },
  {
    icon: "chat_bubble",
    label: "A note from Coach Kiley",
    copy: "Coaching messages and weekly compliance summaries from Kiley.",
    example: "Kiley sent you a message about this week's plan.",
  },
  {
    icon: "groups",
    label: "Live classes and gatherings",
    copy: "Five-minute warning before a live class begins or a gathering opens.",
    example: "Sunrise Flow with Kiley starts in five minutes.",
  },
];

export default function NotificationPermission() {
  const [, navigate] = useLocation();
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: this page WRITES the sister's notification
  // permission choice. See the early return below.
  const userId = me?.id ?? null;
  const prefs = useStoreQuery(() => (userId ? queries.notificationPrefsForUser(userId) : null));
  const setPermission = useStoreMutation(mutations.setNotificationPermission);

  if (!userId || !prefs) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Loading your account...
        </p>
      </div>
    );
  }

  const handleAllow = () => {
    setPermission(userId, "granted");
    navigate("/settings");
  };

  const handleDeny = () => {
    setPermission(userId, "denied");
    navigate("/");
  };

  const handleSkip = () => {
    navigate("/");
  };

  return (
    <div className="pb-24" data-testid="page-notification-permission">
      <ScreenHeader
        variant="forest"
        eyebrow="A small kindness"
        title="May we whisper to you?"
        subtitle="A handful of gentle reminders to help you walk steady. You stay in charge of every one."
      />

      {prefs.permissionStatus !== "unknown" && (
        <div className="px-5 mt-2">
          <GlassCard organic padding={14}>
            <div className="flex items-start gap-3">
              <Icon
                name={prefs.permissionStatus === "granted" ? "check_circle" : "info"}
                size={22}
                color={prefs.permissionStatus === "granted" ? "#1E4A35" : "#A07820"}
              />
              <div className="flex-1 min-w-0">
                <p
                  className="mnf-serif"
                  style={{ color: "var(--mnf-f)", fontSize: 15, fontWeight: 600 }}
                >
                  Notifications are{" "}
                  {prefs.permissionStatus === "granted" ? "on" : "off"}
                </p>
                <p
                  className="mnf-body mt-1"
                  style={{ color: "var(--mnf-body)", fontSize: 13, lineHeight: 1.4 }}
                >
                  You can adjust each category in Settings, or change the system
                  permission anytime in your device settings.
                </p>
                <div className="mt-3 flex gap-2">
                  <Btn
                    variant="primary"
                    onClick={() => navigate("/settings")}
                    data-testid="button-go-settings"
                  >
                    Open Settings
                  </Btn>
                  <Btn
                    variant="ghost"
                    onClick={() => navigate("/")}
                    data-testid="button-back-today"
                  >
                    Back to Today
                  </Btn>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {prefs.permissionStatus === "unknown" && (
        <>
          <div className="px-5 mt-2">
            <GlassCard organic padding={16}>
              <div className="flex items-start gap-3">
                <Icon name="notifications_active" size={22} color="#C0784A" />
                <div className="flex-1 min-w-0">
                  <p
                    className="mnf-serif"
                    style={{ color: "var(--mnf-f)", fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}
                  >
                    What you will hear from us
                  </p>
                  <p
                    className="mnf-body mt-1"
                    style={{ color: "var(--mnf-body)", fontSize: 13, lineHeight: 1.5 }}
                  >
                    No advertising, no third parties. Just these five categories,
                    each one a toggle you can turn off in a tap.
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>

          <div className="px-5 mt-3 space-y-2.5">
            {CATEGORIES.map((cat) => (
              <GlassCard
                key={cat.label}
                organic
                padding={14}
                data-testid={`row-category-${cat.icon}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="shrink-0 flex items-center justify-center"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: "rgba(168, 212, 184, 0.25)",
                    }}
                  >
                    <Icon name={cat.icon} size={20} color="#1E4A35" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="mnf-serif"
                      style={{
                        color: "var(--mnf-f)",
                        fontSize: 15,
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {cat.label}
                    </p>
                    <p
                      className="mnf-body mt-1"
                      style={{ color: "var(--mnf-body)", fontSize: 13, lineHeight: 1.45 }}
                    >
                      {cat.copy}
                    </p>
                    <p
                      className="mnf-body mt-1.5"
                      style={{
                        color: "rgba(45, 62, 48, 0.7)",
                        fontSize: 12,
                        fontStyle: "italic",
                        lineHeight: 1.4,
                      }}
                    >
                      "{cat.example}"
                    </p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>

          <div className="px-5 mt-4">
            <GlassCard organic padding={14}>
              <div className="flex items-start gap-3">
                <Icon name="lock" size={20} color="#1E4A35" />
                <div className="flex-1 min-w-0">
                  <p
                    className="mnf-body"
                    style={{ color: "var(--mnf-body)", fontSize: 13, lineHeight: 1.5 }}
                  >
                    We never include sensitive details (pregnancy, weight,
                    specific health information) in notification text. Anyone
                    glancing at your lock screen sees only a gentle, generic
                    nudge. Read more in our{" "}
                    <a
                      href="#/legal/privacy"
                      style={{ color: "var(--mnf-coral)", textDecoration: "none" }}
                      data-testid="link-privacy-from-permission"
                    >
                      Privacy Policy, Section 9
                    </a>
                    .
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>

          <div className="px-5 mt-5 space-y-2">
            <Btn
              variant="primary"
              block
              onClick={handleAllow}
              data-testid="button-allow-notifications"
            >
              Yes, send me these
            </Btn>
            <Btn
              variant="ghost"
              block
              onClick={handleSkip}
              data-testid="button-skip-notifications"
            >
              Maybe later
            </Btn>
            <button
              type="button"
              onClick={handleDeny}
              className="mnf-body w-full text-center"
              style={{
                color: "rgba(45, 62, 48, 0.55)",
                fontSize: 12,
                background: "transparent",
                border: 0,
                padding: "8px 0",
                cursor: "pointer",
              }}
              data-testid="button-deny-notifications"
            >
              No thank you, do not send any
            </button>
          </div>
        </>
      )}
    </div>
  );
}
